/**
 * Bid Request API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  BidRequest,
  BidRequestListParams,
  BidRequestListResponse,
  BidRequestStats,
  BidComparisonData,
  CreateBidRequestRequest,
  UpdateBidRequestRequest,
  AwardBidRequest,
} from '../types/bidRequest';

/**
 * Bid Request API Endpoints
 */
export const bidRequestApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // =========================================================================
    // LIST
    // =========================================================================

    getBidRequests: builder.query<
      ApiResponse<BidRequestListResponse>,
      { projectId: string; params?: BidRequestListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/bid-requests`,
        params,
      }),
      providesTags: ['BidRequest'],
    }),

    // =========================================================================
    // STATS
    // =========================================================================

    getBidRequestStats: builder.query<
      ApiResponse<BidRequestStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/bid-requests/stats`,
      }),
      providesTags: ['BidRequestStats'],
    }),

    // =========================================================================
    // GET ONE
    // =========================================================================

    getBidRequest: builder.query<
      ApiResponse<BidRequest>,
      { projectId: string; bidRequestId: string }
    >({
      query: ({ projectId, bidRequestId }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}`,
      }),
      providesTags: (_result, _error, { bidRequestId }) => [
        { type: 'BidRequest', id: bidRequestId },
      ],
    }),

    // =========================================================================
    // BID COMPARISON
    // =========================================================================

    getBidComparison: builder.query<
      ApiResponse<BidComparisonData>,
      { projectId: string; bidRequestId: string }
    >({
      query: ({ projectId, bidRequestId }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}/comparison`,
      }),
      providesTags: (_result, _error, { bidRequestId }) => [
        { type: 'BidRequest', id: bidRequestId },
      ],
    }),

    // =========================================================================
    // CREATE
    // =========================================================================

    createBidRequest: builder.mutation<
      ApiResponse<BidRequest>,
      { projectId: string; data: CreateBidRequestRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/bid-requests`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BidRequest', 'BidRequestStats'],
    }),

    // =========================================================================
    // UPDATE
    // =========================================================================

    updateBidRequest: builder.mutation<
      ApiResponse<BidRequest>,
      { projectId: string; bidRequestId: string; data: UpdateBidRequestRequest }
    >({
      query: ({ projectId, bidRequestId, data }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { bidRequestId }) => [
        'BidRequest',
        { type: 'BidRequest', id: bidRequestId },
        'BidRequestStats',
      ],
    }),

    // =========================================================================
    // DELETE
    // =========================================================================

    deleteBidRequest: builder.mutation<
      ApiResponse<void>,
      { projectId: string; bidRequestId: string }
    >({
      query: ({ projectId, bidRequestId }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BidRequest', 'BidRequestStats'],
    }),

    // =========================================================================
    // WORKFLOW
    // =========================================================================

    issueBidRequest: builder.mutation<
      ApiResponse<BidRequest>,
      { projectId: string; bidRequestId: string }
    >({
      query: ({ projectId, bidRequestId }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}/issue`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { bidRequestId }) => [
        'BidRequest',
        { type: 'BidRequest', id: bidRequestId },
        'BidRequestStats',
      ],
    }),

    closeBidRequest: builder.mutation<
      ApiResponse<BidRequest>,
      { projectId: string; bidRequestId: string }
    >({
      query: ({ projectId, bidRequestId }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}/close`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { bidRequestId }) => [
        'BidRequest',
        { type: 'BidRequest', id: bidRequestId },
        'BidRequestStats',
      ],
    }),

    awardBidRequest: builder.mutation<
      ApiResponse<BidRequest>,
      { projectId: string; bidRequestId: string; data: AwardBidRequest }
    >({
      query: ({ projectId, bidRequestId, data }) => ({
        url: `/projects/${projectId}/bid-requests/${bidRequestId}/award`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { bidRequestId }) => [
        'BidRequest',
        { type: 'BidRequest', id: bidRequestId },
        'BidRequestStats',
      ],
    }),
  }),
});

export const {
  useGetBidRequestsQuery,
  useGetBidRequestStatsQuery,
  useGetBidRequestQuery,
  useGetBidComparisonQuery,
  useCreateBidRequestMutation,
  useUpdateBidRequestMutation,
  useDeleteBidRequestMutation,
  useIssueBidRequestMutation,
  useCloseBidRequestMutation,
  useAwardBidRequestMutation,
} = bidRequestApi;
